/**
 * Acquisition tracking — capture UTM parameters and referrer on first visit.
 *
 * Stores first-touch attribution in localStorage so the source that originally
 * brought a user to IndexAlpha persists across sessions and survives until
 * sign-up. Without this, paid acquisition spend cannot be attributed to
 * downstream conversions.
 */

const STORAGE_KEY = 'indexalpha_first_touch';

export interface AcquisitionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  initial_referrer: string | null;
  captured_at: string;
}

const EMPTY: AcquisitionData = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  initial_referrer: null,
  captured_at: new Date(0).toISOString(),
};

/**
 * Read first-touch attribution from localStorage. Returns an empty record
 * with null fields when no first-touch is stored or storage is unavailable.
 */
export function getFirstTouch(): AcquisitionData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<AcquisitionData>;
    return { ...EMPTY, ...parsed };
  } catch {
    return EMPTY;
  }
}

/**
 * Capture UTM parameters and document.referrer once per browser. Subsequent
 * calls are no-ops, so first-touch attribution is preserved.
 *
 * Call this on app mount (App.tsx). Safe to call repeatedly — only the first
 * call with no existing record performs a write.
 */
export function captureAcquisition(): AcquisitionData {
  const existing = getFirstTouch();
  // Already captured — preserve first-touch attribution.
  if (existing.captured_at !== EMPTY.captured_at) return existing;

  let next: AcquisitionData = { ...EMPTY };
  try {
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer ? new URL(document.referrer).hostname : null;

    next = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      initial_referrer: referrer,
      captured_at: new Date().toISOString(),
    };
  } catch {
    return existing;
  }

  // Only persist if at least one signal is present — avoids writing a
  // useless empty record for direct navigations with no referrer.
  const hasSignal =
    next.utm_source ||
    next.utm_medium ||
    next.utm_campaign ||
    next.initial_referrer;
  if (!hasSignal) return existing;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be disabled in private browsing; the in-memory
    // value is still returned to the caller.
  }
  return next;
}

const ANON_ID_KEY = 'indexalpha_anon_id';

/**
 * Generate or fetch a stable anonymous ID for this browser. Used to stitch
 * pre-signup and post-signup activity into a single user journey when the
 * Firebase user ID is set later. SQL in BigQuery can `JOIN` on this ID to
 * trace "what did this user do before they signed up?"
 *
 * The ID is a UUID v4 stored in localStorage. Survives across sessions
 * until the browser/storage is wiped.
 */
export function getAnonymousId(): string {
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const id = `anon_${crypto.randomUUID()}`;
    localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    // Fallback when localStorage is unavailable (private browsing).
    // We return a fresh ID per call — stitching won't work but tracking
    // doesn't crash.
    return `anon_${crypto.randomUUID()}`;
  }
}

/**
 * ISO week string like "2026-W17". Used to bucket users into signup cohorts
 * for retention analysis without exposing exact signup date.
 */
export function isoWeek(date: Date = new Date()): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}
